import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDialogStyles from "../TransferTicketModalCustom/dialogStyles";

const selectMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null,
};

const FinalizacaoVendaModal = ({ open, onClose, ticket, onFinalizar }) => {
  const history = useHistory();
  const classes = useDialogStyles({ width: 460 });
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [vendaConcluida, setVendaConcluida] = useState(true);
  const [valorVenda, setValorVenda] = useState("");
  const [motivoNaoVenda, setMotivoNaoVenda] = useState("");
  const [motivoFinalizacao, setMotivoFinalizacao] = useState("");
  const [motivosFinalizacao, setMotivosFinalizacao] = useState([]);
  const [informarValorVenda, setInformarValorVenda] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);

  const handleClose = () => {
    onClose();
    setVendaConcluida(true);
    setValorVenda("");
    setMotivoNaoVenda("");
    setMotivoFinalizacao("");
  };

  useEffect(() => {
    if (open) {
      setLoadingConfig(true);
      Promise.all([fetchMotivosFinalizacao(), fetchCompanySettings()]).then(
        () => setLoadingConfig(false)
      );
    }
  }, [open]);

  const fetchMotivosFinalizacao = async () => {
    try {
      const { data } = await api.get("/ticketFinalizationReasons");
      setMotivosFinalizacao(data);
    } catch (err) {
      console.error("Erro ao buscar motivos de finalização:", err);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const { data } = await api.get("/companySettings");
      setInformarValorVenda(data.informarValorVenda || false);
    } catch (err) {
      console.error("Erro ao buscar configurações da empresa:", err);
    }
  };

  const handleSubmit = async () => {
    if (informarValorVenda) {
      // Check ativado: lógica antiga
      if (vendaConcluida) {
        if (!valorVenda) {
          toast.error("Por favor, informe o valor da venda.");
          return;
        }
      } else {
        if (!motivoNaoVenda) {
          toast.error("Por favor, selecione o motivo da não venda.");
          return;
        }
      }
    } else {
      // Check desativado: só motivo de finalização
      if (!motivoFinalizacao) {
        toast.error("Por favor, selecione o motivo da finalização.");
        return;
      }
    }

    // Em vez de finalizar aqui, apenas passa os dados para o pai
    const ticketData = {
      status: "closed",
      userId: user?.id || null,
      // sendFarewellMessage e finalizacaoMessage serão definidos no modal seguinte
    };

    if (informarValorVenda) {
      ticketData.finalizadoComVenda = vendaConcluida;
      if (vendaConcluida) {
        ticketData.valorVenda = parseFloat(valorVenda);
        ticketData.motivoNaoVenda = null;
        ticketData.motivoFinalizacao = null;
      } else {
        ticketData.valorVenda = null;
        ticketData.motivoNaoVenda = motivoNaoVenda;
        ticketData.motivoFinalizacao = null;
      }
    } else {
      ticketData.finalizadoComVenda = null;
      ticketData.valorVenda = null;
      ticketData.motivoNaoVenda = null;
      ticketData.motivoFinalizacao = motivoFinalizacao;
    }

    // Chama o callback passando os dados
    if (onFinalizar) onFinalizar(ticketData);
    handleClose();
  };

  // mesmo seletor para "motivo da não venda" e "motivo da finalização"
  const renderMotivoSelect = (value, onChange) => (
    <Select
      fullWidth
      displayEmpty
      variant="outlined"
      margin="dense"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      MenuProps={selectMenuProps}
      renderValue={(selected) =>
        selected ? selected : <span className={classes.placeholder}>Selecione um motivo</span>
      }
    >
      <MenuItem dense value="">
        <em>Selecione um motivo</em>
      </MenuItem>
      {motivosFinalizacao.map((motivo) => (
        <MenuItem dense key={motivo.id} value={motivo.name}>
          {motivo.name}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
      aria-labelledby="finalizacao-venda-title"
    >
      <div className={classes.header}>
        <h2 id="finalizacao-venda-title" className={classes.title}>
          Finalizar atendimento
        </h2>
        <p className={classes.subtitle}>
          {informarValorVenda
            ? "Informe se houve venda antes de encerrar a conversa."
            : "Informe o motivo antes de encerrar a conversa."}
        </p>
      </div>
      {loadingConfig ? (
        <DialogContent className={classes.content}>
          <div className={classes.loading}>
            <CircularProgress size={18} color="inherit" />
            Carregando configurações...
          </div>
        </DialogContent>
      ) : (
        <DialogContent className={`${classes.content} ${classes.contentVisible}`}>
          {informarValorVenda ? (
            <>
              <div className={classes.field}>
                <span className={classes.label}>A venda foi concluída?</span>
                <RadioGroup
                  value={vendaConcluida}
                  onChange={(e) =>
                    setVendaConcluida(e.target.value === "true")
                  }
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio color="primary" size="small" />}
                    label="Sim, venda concluída"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio color="primary" size="small" />}
                    label="Não, venda não concluída"
                  />
                </RadioGroup>
              </div>
              {vendaConcluida ? (
                <div className={classes.field}>
                  <span className={classes.label}>Valor da venda (R$)</span>
                  <TextField
                    type="number"
                    value={valorVenda}
                    onChange={(e) => setValorVenda(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    inputProps={{
                      step: "0.01",
                      min: "0",
                      "aria-label": "Valor da venda (R$)",
                    }}
                    placeholder="0,00"
                  />
                </div>
              ) : (
                <div className={classes.field}>
                  <span className={classes.label}>Motivo da não venda *</span>
                  {renderMotivoSelect(motivoNaoVenda, setMotivoNaoVenda)}
                </div>
              )}
            </>
          ) : (
            <div className={classes.field}>
              <span className={classes.label}>Motivo da finalização *</span>
              {renderMotivoSelect(motivoFinalizacao, setMotivoFinalizacao)}
            </div>
          )}
        </DialogContent>
      )}
      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} disabled={loading} className={classes.cancel}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || loadingConfig}
          className={classes.confirm}
        >
          {loading ? "Finalizando..." : "Finalizar Atendimento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizacaoVendaModal;
